"use client";
import React, { useState } from "react";
import { MobileHeader } from "../MobileHeader";
import MetricCard from "../MetricCard";
import TipSection from "../TipSection";
import ChallengeSection from "../ChallengeSection";
import BadgeSection from "../BadgeSection";
import { TabBar } from "../TabBar";
import { AreaComparisonChart } from "../AreaComparison";

function ConservationDashboard() {
  // Sample data for water saving history graph
  const savingsData = [
    { date: 'Mon', value: 21 },
    { date: 'Tue', value: 30 },
    { date: 'Wed', value: 18 },
    { date: 'Thu', value: 25 },
    { date: 'Fri', value: 32 },
    { date: 'Sat', value: 16 },
    { date: 'Sun', value: 15 },
  ];

  // Calculate total savings for the week
  const totalSavings = savingsData.reduce((acc, curr) => acc + curr.value, 0);

  return (
    <main className="pb-96 mx-auto w-full max-w-[480px]">
      <div className="overflow-hidden w-full bg-white">
        <MobileHeader />
        <section className="pt-8 pb-12 w-full">
          <header className="w-full text-2xl font-semibold leading-none text-gray-900">
            <div className="px-4 w-full">
              <div className="w-full">
                <div className="flex flex-col justify-center w-full">
                  <h1 className="w-full">
                    Conservation Hub
                  </h1>
                </div>
              </div>
            </div>
          </header>

          <section className="mt-8 w-full">
            <div className="px-4 w-full">
              {/* Header banner with animation */}
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200 mb-6">
                <div className="flex items-center gap-4">
                  <div className="bg-blue-600 rounded-full p-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Your Water Saving Impact</h2>
                    <p className="text-sm text-gray-600">Track your conservation journey and make a difference</p>
                  </div>
                </div>
              </div>
              
              {/* Savings metric card */}
              <MetricCard
                title="This Week's Savings"
                value={`${totalSavings}L`}
                changePercentage="13%"
                changeDirection="up"
                comparisonText="vs last month"
                chartImageUrl="https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/9074e36457e367090fbacec4ec7cc9f1161ab538?placeholderIfAbsent=true"
              />
              
              {/* Savings visualization */}
              <div className="mt-6 bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Savings</h3>
                <div className="h-40">
                  <div className="flex items-end justify-between h-32">
                    {savingsData.map((item, index) => (
                      <div key={index} className="flex flex-col items-center">
                        <div 
                          className="w-8 bg-blue-500 rounded-t-md" 
                          style={{ height: `${(item.value / 35) * 100}%` }}
                        ></div>
                        <span className="text-xs text-gray-600 mt-2">{item.date}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Personalized tips section */}
              <TipSection />

              {/* Challenges section */}
              <ChallengeSection />
              
              {/* Community impact */}
              <div className="mt-6 bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Community Impact</h3>
                <p className="text-sm text-gray-600 mb-4">See how your neighborhood is saving water together</p>
                
                <div className="flex justify-between items-center bg-blue-50 rounded-lg p-4 mb-4">
                  <div>
                    <span className="text-sm font-medium text-gray-600">Neighborhood Goal</span>
                    <h4 className="text-xl font-semibold text-gray-900">50,000L</h4>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">Current Savings</span>
                    <h4 className="text-xl font-semibold text-blue-600">32,458L</h4>
                  </div>
                </div>
                
                {/* Progress bar */}
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: '65%' }}></div>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-gray-500">0L</span>
                  <span className="text-xs text-gray-500">50,000L</span>
                </div>
              </div>
              
              {/* Area Comparison */}
              <div className="mt-8 mb-8 bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Area Comparison</h3>
                  <select 
                    className="text-sm border border-gray-300 rounded-md px-2 py-1 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    defaultValue="week"
                  >
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="year">This Year</option>
                  </select>
                </div>
                <p className="text-sm text-gray-600 mb-6">Compare your water usage with similar homes in your area</p>
                
                {/* Set a fixed height to prevent layout shifts */}
                <div className="mb-8"> 
                  <AreaComparisonChart className="h-auto" />
                </div>
              </div>
              
              {/* Info Box - Separated from Area Comparison chart */}
              <div className="mt-4 mb-8 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 p-1 bg-blue-100 rounded-full mt-0.5">
                    <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-blue-800">Your household uses <span className="font-medium">15% less water</span> than similar homes in your area.</p>
                  </div>
                </div>
              </div>

              {/* Badges section - With clear separation */}
              <div className="mt-10 pt-4 border-t border-gray-200">
                <h3 className="text-xl font-bold mb-6">Your Badges</h3>
                {/* Use BadgeSection with showTitle=false to avoid duplicate titles */}
                <div className="mt-2">
                  <BadgeSection showTitle={false} />
                </div>
              </div>
              
              {/* Tips button */}
              <div className="mt-6">
                <a href="/conservation-tips" className="w-full block bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg px-5 py-3.5 text-center transition duration-150 ease-in-out">
                  View All Conservation Tips
                </a>
              </div>
            </div>
          </section>
        </section>
      </div>

      <TabBar />
    </main>
  );
}

export default ConservationDashboard;
